import { getSessionManager } from '@/lib/SessionTracking/SessionManager';
import { getBrowserContext } from '@/lib/SessionTracking/browserContext';
import { isSessionStateExpiredError } from '@/lib/SessionTracking/sessionStateErrors';
import {
  getForceLogoutReasonFromHeaders,
  isSessionIdentityMismatchMessage,
  triggerClientForceLogout,
} from '@/lib/Auth/clientForceLogout';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { SessionStateEventType } from 'types/sessionStateLog';

type AxiosErrorWithMeta = AxiosError & {
  requestId?: string;
  errorId?: string;
};

declare module 'axios' {
  export interface AxiosRequestConfig {
    suppressExpectedErrorLogging?: boolean;
  }
}

type NoisyEndpointPolicy = {
  id: string;
  pattern: RegExp;
  cooldownMs: number;
  sampleRate: number;
};

const noisyEndpointPolicies: NoisyEndpointPolicy[] = [
  {
    id: 'session-event',
    pattern: /\/api\/session\/event/i,
    cooldownMs: 60_000,
    sampleRate: 0.1,
  },
];

const noisyEndpointState = new Map<string, number>();

const shouldRecordApiBreadcrumb = (url: string): boolean => {
  const policy = noisyEndpointPolicies.find((candidate) =>
    candidate.pattern.test(url)
  );
  if (!policy) {
    return true;
  }

  const now = Date.now();
  const lastRecorded = noisyEndpointState.get(policy.id) ?? 0;
  if (now - lastRecorded < policy.cooldownMs) {
    if (Math.random() < policy.sampleRate) {
      noisyEndpointState.set(policy.id, now);
      return true;
    }
    return false;
  }

  noisyEndpointState.set(policy.id, now);
  return true;
};

const shouldSuppressExpectedErrorLogging = (
  config: AxiosRequestConfig,
  status: number
): boolean =>
  config.suppressExpectedErrorLogging === true && status >= 400 && status < 500;

const axiosInstance = axios.create({
  // In the browser, prefer same-origin requests so NextAuth cookies are sent reliably.
  // In Node (SSR/scripts), fall back to an absolute URL when provided.
  baseURL:
    typeof window === 'undefined'
      ? process.env.NEXT_PUBLIC_BASE_URL
      : undefined,
});

/**
 * Breadcrumb sampling strategy:
 * - successful responses only update session metrics (no breadcrumbs)
 * - failures record a breadcrumb unless the endpoint is throttled/sampled above
 */
axiosInstance.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const sessionManager = getSessionManager();
    const sessionId = sessionManager?.getSessionId();
    if (sessionId) {
      const headers = AxiosHeaders.from(config.headers ?? {});
      headers.set('x-session-id', sessionId);
      config.headers = headers;
    }
    (config as any).__startTime = Date.now();
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Record SYSTEM_FLOW on successful responses
    if (typeof window !== 'undefined') {
      const start = (response.config as any).__startTime;
      const durationMs =
        typeof start === 'number' ? Math.max(Date.now() - start, 0) : undefined;

      const sessionManager = getSessionManager();
      if (sessionManager?.isActive()) {
        const endpoint = response.config.url || 'unknown';
        const method = response.config.method?.toUpperCase() || 'GET';
        const status = response.status;
        const requestId =
          (response.headers['x-request-id'] as string | undefined) ||
          (response.headers['x-correlation-id'] as string | undefined);

        sessionManager.apiCall(endpoint, method, status, {
          durationMs,
          requestId,
        });
      }
    }
    return response;
  },
  async (error: AxiosErrorWithMeta) => {
    try {
      const config: AxiosRequestConfig = error.config || {};
      const url = config.url || 'unknown';
      const status = error.response?.status ?? 0;
      const suppressExpectedErrorLogging = shouldSuppressExpectedErrorLogging(
        config,
        status
      );

      const shouldRecord = shouldRecordApiBreadcrumb(url);
      const sessionManager = getSessionManager();
      // Note: We intentionally don't buffer apiCall here for errors.
      // The ERROR_REPORT sent below already captures full context including breadcrumbs.
      // Buffering would create duplicate events (SYSTEM_FLOW + ERROR_REPORT for same failure).

      const headers = error.response?.headers ?? {};
      const requestId =
        (headers['x-request-id'] as string | undefined) ||
        (headers['x-correlation-id'] as string | undefined);
      const errorId = headers['x-error-id'] as string | undefined;

      if (requestId) {
        error.requestId = requestId;
      }
      if (errorId) {
        error.errorId = errorId;
      }

      const responseMessage =
        typeof (error.response?.data as { message?: unknown } | undefined)
          ?.message === 'string'
          ? (error.response?.data as { message: string }).message
          : undefined;
      const forceLogoutReason = getForceLogoutReasonFromHeaders(
        headers as Record<string, string | string[] | undefined>
      );
      const shouldForceLogout =
        Boolean(forceLogoutReason) ||
        isSessionIdentityMismatchMessage(responseMessage);

      if (typeof window !== 'undefined' && shouldForceLogout) {
        triggerClientForceLogout(
          forceLogoutReason ?? 'session_state_user_id_mismatch'
        );
        return Promise.reject(error);
      }

      if (typeof window !== 'undefined' && !suppressExpectedErrorLogging) {
        const context = {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          requestId,
          errorId,
        };
        // eslint-disable-next-line no-console
        console.error('HTTP request failed', context);

        if (shouldRecord && (errorId || requestId)) {
          try {
            const Sentry = await import('@sentry/nextjs');
            Sentry.addBreadcrumb({
              category: 'api',
              level: 'error',
              message: 'Client API error',
              data: context,
            });
            Sentry.setTag('api.requestId', requestId || 'unknown');
            if (errorId) {
              Sentry.setTag('api.errorId', errorId);
            }
          } catch {
            // Sentry not configured; ignore
          }
        }
      }

      if (typeof window !== 'undefined' && !suppressExpectedErrorLogging) {
        const sessionId = sessionManager?.getSessionId();
        const userId = sessionManager?.getUserId();
        const breadcrumbs = sessionManager?.getBreadcrumbs(10) ?? [];
        const now = new Date().toISOString();
        const context = getBrowserContext();
        const device = context.device ?? 'unknown';
        const responseBody =
          typeof error.response?.data === 'string'
            ? error.response.data
            : error.response?.data
              ? JSON.stringify(error.response.data)
              : undefined;
        const responseExcerpt =
          responseBody && responseBody.length > 2000
            ? `${responseBody.slice(0, 2000)}…`
            : responseBody;
        void import('@/lib/SessionTracking/sessionStateUploader').then(
          ({ sendSessionStateEvents }) =>
            sendSessionStateEvents([
              {
                sessionId: sessionId || 'unknown-session',
                userId: userId ?? undefined,
                clientTimestamp: now,
                platform: 'WEB',
                severity: 'ERROR',
                eventType: SessionStateEventType.ERROR_REPORT,
                eventName: `HTTP_${status || 'ERR'}`,
                eventPayload: {
                  event_name: `HTTP_${status || 'ERR'}`,
                  is_critical: status >= 500,
                  platform: 'web',
                  device,
                  osVersion: context.osVersion,
                  scene: context.scene,
                  assignmentId: context.assignmentId,
                  timestamp: now,
                  status_code: status || 0,
                  error_message: error.message,
                  stack_trace: error.stack,
                  breadcrumbs,
                  request_id: requestId || errorId,
                  response_excerpt: responseExcerpt,
                  online: context.online,
                  connection: context.connection,
                },
              },
            ]).catch(async (sendError) => {
              if (sessionManager && isSessionStateExpiredError(sendError)) {
                await sessionManager.handleSessionExpired();
              }
              // Swallow upload errors; HTTP failure logging must not block app flow
            })
        );
      }
    } catch (interceptorError) {
      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.error('Axios error interceptor failure', interceptorError);
      }
    }

    return Promise.reject(error);
  }
);

export const getAxiosErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'message' in data &&
      typeof (data as Record<string, unknown>).message === 'string'
    ) {
      return (data as { message: string }).message;
    }
    if (
      typeof data === 'object' &&
      data !== null &&
      'issues' in data &&
      Array.isArray((data as { issues?: unknown }).issues)
    ) {
      const issues = (data as { issues?: Array<{ message?: unknown }> }).issues;
      const issueMessage =
        issues && issues.length > 0 ? issues[0]?.message : undefined;
      if (typeof issueMessage === 'string' && issueMessage.trim().length > 0) {
        return issueMessage;
      }
    }
    return error.message || 'Unknown error';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error';
};

export default axiosInstance;
